import type { JSX } from 'react';
import { planCapacitySchema } from '@cryptology-assist/domain';
import { useRemote } from './hooks';
import { ErrorNotice } from './components';
import { dateLabel, numberLabel, type Copy, type Language } from './copy';

// Shown on the pages of an assistant whose plan is running out, is used up or
// has no active period: in the last two cases it answers no new customer.
export function PlanCapacityNotice({
  path,
  copy,
  language,
  onOpen,
}: {
  path: string;
  copy: Copy;
  language: Language;
  // Leads to the usage page; absent on that page itself.
  onOpen: (() => void) | null;
}): JSX.Element | null {
  const capacity = useRemote(path, planCapacitySchema);
  if (capacity.error)
    return (
      <ErrorNotice
        error={capacity.error}
        copy={copy}
        onRetry={capacity.reload}
      />
    );
  if (!capacity.data || capacity.data.state === 'available') return null;
  const { state, used, included, periodEndsAt } = capacity.data;
  return (
    <div
      className={state === 'low' ? 'notice' : 'notice error'}
      role={state === 'low' ? 'status' : 'alert'}
    >
      <strong>
        {state === 'low'
          ? copy.planCapacityLow
          : state === 'exhausted'
            ? copy.planCapacityExhausted
            : copy.planCapacityInactive}
      </strong>
      <span>
        {copy.conversationsUsed}: {numberLabel(String(used), language)} /{' '}
        {numberLabel(String(included), language)} · {copy.planPeriodEnds}:{' '}
        {dateLabel(periodEndsAt, language)}
      </span>
      {state === 'exhausted' && <span>{copy.planCapacityExhaustedHelp}</span>}
      {onOpen && <button onClick={onOpen}>{copy.planCapacityOpen}</button>}
    </div>
  );
}
